import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { PayModal, usePayStore } from '@/components/common/pay';
import { useAuth } from '@/hooks/useAuth';
import { useIdempotencyKey } from '@/hooks/useIdempotencyKey';
import {
  fetchMembershipPlans,
  fetchPointProducts,
  createMembershipOrder,
  createPointOrder
} from '@/service/api/membership';

export interface MembershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: 'membership' | 'points';
  defaultMemberType?: 'personal' | 'enterprise';
}

export default function MembershipModal({
  isOpen,
  onClose,
  defaultTab = 'membership',
  defaultMemberType = 'personal'
}: MembershipModalProps) {
  const { user, rawUser, isLoggedIn } = useAuth();
  const { generateKey } = useIdempotencyKey();

  // 当前已开通会员套餐 ID 与免费状态判断
  const currentPlanId = rawUser?.membership?.subscriptions?.[0]?.plan_id || (user as any)?.membership?.subscriptions?.[0]?.plan_id;
  const isCurrentPlanFree = !currentPlanId;

  // 页签与筛选状态
  const [activeTab, setActiveTab] = useState<'membership' | 'points'>(defaultTab);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly' | 'forever'>('monthly');

  // 从 authStore/user 中自动判断账户类型是否为企业账户
  const isEnterprise = useMemo(() => {
    if (!user) return false;
    const type = (user.accountType || (user as any).account_type || '').toString().toUpperCase();
    return type === 'ENTERPRISE';
  }, [user]);

  const memberType: 'personal' | 'enterprise' = isEnterprise ? 'enterprise' : 'personal';

  // 当弹窗属性变化时重置/设置默认页签
  useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab);
    }
  }, [isOpen, defaultTab]);

  // 会员套餐 API 数据
  const [plans, setPlans] = useState<Api.Membership.PlanResponse[]>([]);
  const [loadingPlans, setLoadingPlans] = useState<boolean>(true);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  // 积分包 API 数据
  const [products, setProducts] = useState<Api.Membership.PointProductResponse[]>([]);
  const [loadingProducts, setLoadingProducts] = useState<boolean>(true);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  // 操作与通知状态
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [notice, setNotice] = useState<string | null>(null);

  // 获取可购买会员套餐 API (GET /membership/plans)
  useEffect(() => {
    if (!isOpen) return;
    const loadPlans = async () => {
      try {
        setLoadingPlans(true);
        const res = await fetchMembershipPlans({
          subject_type: isEnterprise ? 'ENTERPRISE' : 'PERSONAL'
        });
        const list = res?.plans || [];
        setPlans(list);
        if (list.length > 0) {
          setSelectedPlanId(list[0].id || null);
        }
      } catch (err) {
        console.error('获取会员套餐失败:', err);
      } finally {
        setLoadingPlans(false);
      }
    };
    loadPlans();
  }, [isOpen, isEnterprise]);

  // 获取积分商品 API (GET /points/products)
  useEffect(() => {
    if (!isOpen) return;
    const loadProducts = async () => {
      try {
        setLoadingProducts(true);
        const res = await fetchPointProducts();
        const list = res?.products || [];
        setProducts(list);
        if (list.length > 0) {
          setSelectedProductId(list[0].id || null);
        }
      } catch (err) {
        console.error('获取积分商品失败:', err);
      } finally {
        setLoadingProducts(false);
      }
    };
    loadProducts();
  }, [isOpen]);

  // 锁定背景 Body 滚动，防止滚动穿透与滚动抢占
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // 监听 Esc 按键关闭弹窗
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // 当为企业账号时，如果当前选择的是永久买断，则重置为按月付
  useEffect(() => {
    if (isEnterprise && billingCycle === 'forever') {
      setBillingCycle('monthly');
    }
  }, [isEnterprise, billingCycle]);

  // 过滤展示的 API 套餐列表
  const visiblePlans = useMemo(() => {
    return plans.filter((p) => {
      const pCycle = (p.billing_cycle || p.duration_type || '').toUpperCase();
      if (billingCycle === 'monthly') {
        return pCycle.includes('MONTH') || pCycle === 'MONTHLY' || !p.billing_cycle;
      }
      if (billingCycle === 'yearly') {
        return pCycle.includes('YEAR') || pCycle.includes('ANNUAL') || pCycle === 'YEARLY';
      }
      if (billingCycle === 'forever') {
        return pCycle.includes('PERMANENT') || pCycle.includes('FOREVER') || pCycle.includes('LIFE');
      }
      return true;
    });
  }, [plans, billingCycle]);

  // 创建会员订单并呼出支付弹窗
  const handleOrderPlan = async (planId: string) => {
    if (!isLoggedIn) {
      setNotice('请先登录账号后再进行购买');
      return;
    }
    try {
      setSubmitting(true);
      setNotice(null);
      const res = await createMembershipOrder({
        plan_id: planId,
        idempotency_key: generateKey(`plan_${planId}`)
      });

      if (res && (res.id || res.order_no)) {
        const orderId = res.id || res.order_no!;
        usePayStore.getState().createPaymentAndOpenModal(
          orderId,
          'membership',
          'WECHAT',
          res.amount
        );
      } else {
        setNotice('订单创建未返回有效编号');
      }
    } catch (err: any) {
      console.error('创建会员订单失败:', err);
      setNotice(err?.message || '创建订单失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  // 创建积分订单并呼出支付弹窗
  const handleOrderPoint = async (productId: string) => {
    if (!isLoggedIn) {
      setNotice('请先登录账号后再进行购买');
      return;
    }
    try {
      setSubmitting(true);
      setNotice(null);
      const res = await createPointOrder({
        product_id: productId,
        idempotency_key: generateKey(`point_${productId}`)
      });

      if (res && (res.id || res.order_no)) {
        const orderId = res.id || res.order_no!;
        usePayStore.getState().createPaymentAndOpenModal(
          orderId,
          'point',
          'WECHAT',
          res.amount
        );
      } else {
        setNotice('订单创建未返回有效编号');
      }
    } catch (err: any) {
      console.error('创建积分订单失败:', err);
      setNotice(err?.message || '创建订单失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // 权益对比表格预设数据
  const compareRows = [
    { name: 'VIP 视频观看', free: '✕', personal: '<span class="text-[#1E8A4C] font-bold">✓</span>', enterprise: '<span class="text-[#6D54C9] font-bold">✓</span>' },
    { name: '赠送积分', free: '—', personal: '1000 / 月（每月重置）', enterprise: '2000 积分' },
    { name: '简历联系方式', free: '✕', personal: '✕', enterprise: '<span class="text-[#6D54C9] font-bold">✓ 直接查看</span>' },
    { name: '专属徽章', free: '✕', personal: '<span class="text-[#1E8A4C] font-bold">✓</span>', enterprise: '<span class="text-[#6D54C9] font-bold">✓</span>' },
    { name: '客服支持', free: '标准', personal: '优先', enterprise: '专属客户经理' },
  ];

  // 兜底及预设个人/企业权益描述
  const fallbackFeatures = isEnterprise ? [
    '畅看全部 <b class="text-[#173C35]">VIP 视频</b>内容',
    '查看人才简历的<b class="text-[#173C35]">联系方式</b>，直连候选人',
    '默认赠送 <b class="text-[#173C35]">2000 积分</b>',
    '企业团队专属管理后台',
    '专属客户经理服务',
  ] : [
    '畅看全部 <b class="text-[#173C35]">VIP 视频</b>内容',
    '每月赠送 <b class="text-[#173C35]">1000 积分</b>，每月自动重置',
    '会员专属徽章标识',
    '优先客服支持',
  ];

  return createPortal(
    <div className="fixed inset-0 z-[1000] w-full h-full min-h-screen bg-white overflow-y-auto overscroll-contain flex flex-col pointer-events-auto text-[#173C35] animate-fade-in">
      {/* 固定右上角关闭按钮 */}
      <button
        type="button"
        onClick={onClose}
        className="fixed top-5 right-6 z-[1010] w-10 h-10 rounded-full bg-white/90 hover:bg-[#EEF7F3] border border-[#DCEAE5] text-[#173C35] transition-all flex items-center justify-center shadow-md cursor-pointer hover:scale-105 active:scale-95"
        title="关闭 (Esc)"
      >
        <i className="ri-close-line text-2xl"></i>
      </button>

      {/* Hero 顶部：全屏铺开并保持内容居中 */}
      <div
        className="relative shrink-0 overflow-hidden border-b border-[#DCEAE5] pt-14 sm:pt-16 pb-16"
        style={{
          background: `
            radial-gradient(circle at 15% 12%, rgba(46, 175, 102, .10), transparent 28rem),
            radial-gradient(circle at 88% 12%, rgba(109, 84, 201, .08), transparent 24rem),
            radial-gradient(circle at 64% 100%, rgba(221, 163, 50, .08), transparent 22rem),
            #FFFFFF
          `
        }}
      >
        <div className="relative max-w-4xl mx-auto px-5 text-center">
            {/* 标志 & 标题 & 描述文案（独占纵向块级空间，防止布局挤压或塌陷） */}
            <div className="flex flex-col items-center justify-center py-2">
              <span
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full border transition-all ${
                  isEnterprise
                    ? 'text-[#6D54C9] border-[#6D54C9]/20 bg-[#F1EDFF]/70'
                    : 'text-[#1E8A4C] border-[#2EAF66]/25 bg-white/70 shadow-xs'
                }`}
              >
                {isEnterprise ? '👑 企业会员专享' : '✦ 灵搭岛 VIP 会员'}
              </span>

              <h2
                className={`mt-2.5 text-2xl sm:text-3xl font-bold tracking-tight bg-clip-text text-transparent ${
                  isEnterprise
                    ? 'bg-gradient-to-r from-[#5540A8] to-[#8069DA]'
                    : 'bg-gradient-to-r from-[#14532D] to-[#1E8A4C]'
                }`}
              >
                {isEnterprise ? '连接人才，助力企业高效招聘' : '开通会员，解锁全部 VIP 能力'}
              </h2>

              <p className="mt-2 text-[#647C75] text-xs sm:text-sm">
                {isEnterprise
                  ? 'VIP 视频随心看 · 直连人才简历联系方式 · 默认赠送 2000 积分'
                  : 'VIP 视频随心看 · 每月 1000 积分自动重置'}
              </p>
            </div>

            {notice && (
              <div className="mt-4 max-w-xl mx-auto p-3 rounded-2xl bg-[#FFF3DD] border border-[#DDA332]/30 text-[#A86100] text-xs sm:text-sm flex items-center justify-between shadow-sm">
                <div className="flex items-center gap-2">
                  <i className="ri-information-line text-base"></i>
                  <span>{notice}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setNotice(null)}
                  className="text-[#A86100] hover:opacity-80 p-1 cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 全屏主体内容 (居中容器) */}
        <main className="max-w-6xl mx-auto w-full px-5 sm:px-8 pb-16 flex-1">
          {/* 页面级 Tabs：开通会员 / 积分充值 */}
          <div className="flex justify-center -mt-5 relative z-10">
            <div className="inline-flex bg-white border border-[#DCEAE5] rounded-full p-1.5 shadow-lg shadow-[#173C35]/5">
              <button
                type="button"
                onClick={() => {
                  setActiveTab('membership');
                  setSelectedProductId(null);
                }}
                className={`px-6 sm:px-8 py-2 rounded-full text-xs sm:text-sm font-bold transition-all duration-200 cursor-pointer ${
                  activeTab === 'membership'
                    ? 'bg-gradient-to-r from-[#1E8A4C] to-[#2EAF66] text-white shadow-sm'
                    : 'text-[#647C75] hover:text-[#173C35]'
                }`}
              >
                开通会员
              </button>
              <button
                type="button"
                onClick={() => {
                  setActiveTab('points');
                  setSelectedPlanId(null);
                }}
                className={`px-6 sm:px-8 py-2 rounded-full text-xs sm:text-sm font-bold transition-all duration-200 cursor-pointer ${
                  activeTab === 'points'
                    ? 'bg-gradient-to-r from-[#2B5CB8] to-[#5B91F0] text-white shadow-sm'
                    : 'text-[#647C75] hover:text-[#173C35]'
                }`}
              >
                积分充值
              </button>
            </div>
          </div>

          {/* ============ Tab 1：开通会员 ============ */}
          {activeTab === 'membership' && (
            <div key="vip" className="animate-fade-in">
              {/* 周期切换 */}
              <div className="flex justify-center mt-6">
                <div className="flex bg-[#EEF7F3] rounded-full p-1 border border-[#DCEAE5]">
                  <button
                    type="button"
                    onClick={() => setBillingCycle('monthly')}
                    className={`relative px-5 py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-200 cursor-pointer ${
                      billingCycle === 'monthly'
                        ? isEnterprise
                          ? 'bg-gradient-to-r from-[#5540A8] to-[#8069DA] text-white shadow'
                          : 'bg-gradient-to-r from-[#1E8A4C] to-[#2EAF66] text-white shadow'
                        : 'text-[#647C75] hover:text-[#173C35]'
                    }`}
                  >
                    按月付
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingCycle('yearly')}
                    className={`relative px-5 py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-200 cursor-pointer ${
                      billingCycle === 'yearly'
                        ? isEnterprise
                          ? 'bg-gradient-to-r from-[#5540A8] to-[#8069DA] text-white shadow'
                          : 'bg-gradient-to-r from-[#1E8A4C] to-[#2EAF66] text-white shadow'
                        : 'text-[#647C75] hover:text-[#173C35]'
                    }`}
                  >
                    按年付
                    <span className="absolute -top-2 -right-1 text-[9px] font-bold text-white bg-[#D96872] px-1.5 py-0.5 rounded-full shadow">
                      -20%
                    </span>
                  </button>
                  {!isEnterprise && (
                    <button
                      type="button"
                      onClick={() => setBillingCycle('forever')}
                      className={`relative px-5 py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-200 cursor-pointer ${
                        billingCycle === 'forever'
                          ? 'bg-gradient-to-r from-[#FFB648] to-[#F2701D] text-white shadow'
                          : 'text-[#647C75] hover:text-[#173C35]'
                      }`}
                    >
                      永久
                    </button>
                  )}
                </div>
              </div>

              {/* 会员卡片 */}
              <div className="grid md:grid-cols-2 gap-5 mt-7">
                {/* 免费版卡片 */}
                <div className="bg-white rounded-3xl border border-[#DCEAE5] p-6 flex flex-col hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#173C35]">免费版</h3>
                    {isCurrentPlanFree && (
                      <span className="text-xs text-[#8DA29C] bg-[#EEF7F3] px-3 py-0.5 rounded-full font-medium">
                        当前方案
                      </span>
                    )}
                  </div>
                  <div className="mt-4 flex items-end gap-1">
                    <span className="text-3xl font-bold text-[#647C75]">¥0</span>
                    <span className="text-[#8DA29C] text-xs mb-1">/ 永久</span>
                  </div>
                  <ul className="mt-5 space-y-3 text-xs sm:text-sm text-[#647C75] flex-1">
                    <li className="flex items-start gap-2">
                      <span className="shrink-0 text-[#3267C5] font-bold">✓</span>
                      <span>浏览公开视频内容</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="shrink-0 text-[#6D54C9] font-bold">✓</span>
                      <span>基础创意工具</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#8DA29C]">
                      <span className="shrink-0">✕</span>
                      <span>无法观看 VIP 视频</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#8DA29C]">
                      <span className="shrink-0">✕</span>
                      <span>无赠送积分</span>
                    </li>
                  </ul>
                  <button
                    disabled
                    className="mt-6 w-full py-2.5 rounded-full border border-[#DCEAE5] text-[#8DA29C] text-xs sm:text-sm font-medium cursor-default bg-[#FFFFFF]"
                  >
                    {isCurrentPlanFree ? '当前方案' : '免费使用中'}
                  </button>
                </div>

                {/* 付费会员卡片 */}
                {loadingPlans ? (
                  <div className="bg-white rounded-3xl border border-[#DCEAE5] p-6 flex flex-col items-center justify-center min-h-[320px]">
                    <div className="w-8 h-8 border-4 border-[#7ED9A6] border-t-[#1E8A4C] rounded-full animate-spin mb-3"></div>
                    <p className="text-xs text-[#8DA29C]">正在加载最新会员套餐...</p>
                  </div>
                ) : (
                  (() => {
                    const activePlan = visiblePlans.find(p => p.id === selectedPlanId) || visiblePlans[0] || plans[0];
                    const isCurrentPlanActive = Boolean(
                      currentPlanId &&
                      activePlan &&
                      (activePlan.id === currentPlanId ||
                       activePlan.plan_code === currentPlanId ||
                       (user?.isVip && (isEnterprise ? user?.isEnterprise : !user?.isEnterprise)))
                    );
                    
                    const priceVal = activePlan?.price?.amount 
                      ? Number(activePlan.price.amount)
                      : (isEnterprise ? 99 : (billingCycle === 'forever' ? 499 : 30));

                    const displayYearlyPrice = Math.round(priceVal * 12 * 0.8);
                    const showPrice = billingCycle === 'yearly' ? displayYearlyPrice : priceVal;

                    const titleText = billingCycle === 'forever' && !isEnterprise
                      ? '永久会员'
                      : isEnterprise ? '企业会员' : '个人会员';

                    const borderClass = isEnterprise
                      ? 'border-[#6D54C9] shadow-[0_12px_30px_-18px_rgba(109,84,201,0.30)]'
                      : (billingCycle === 'forever'
                        ? 'border-[#F2701D] shadow-[0_12px_30px_-18px_rgba(242,112,29,0.35)]'
                        : 'border-[#2EAF66] shadow-[0_12px_30px_-18px_rgba(46,175,102,0.32)]');

                    const textGradClass = isEnterprise
                      ? 'bg-gradient-to-r from-[#5540A8] to-[#8069DA]'
                      : (billingCycle === 'forever'
                        ? 'bg-gradient-to-r from-[#FFB648] to-[#F2701D]'
                        : 'bg-gradient-to-r from-[#1E8A4C] to-[#2EAF66]');

                    const btnGradClass = isEnterprise
                      ? 'bg-gradient-to-r from-[#5540A8] to-[#8069DA] shadow-[0_12px_30px_-18px_rgba(109,84,201,0.30)]'
                      : (billingCycle === 'forever'
                        ? 'bg-gradient-to-r from-[#FFB648] to-[#F2701D] shadow-[0_12px_30px_-18px_rgba(242,112,29,0.35)]'
                        : 'bg-gradient-to-r from-[#1E8A4C] to-[#2EAF66] shadow-[0_12px_30px_-18px_rgba(46,175,102,0.32)]');

                    return (
                      <div
                        className={`relative rounded-3xl p-6 flex flex-col border-2 overflow-hidden transition-all bg-white ${borderClass}`}
                      >
                        <div
                          className={`absolute top-0 right-0 text-[11px] font-bold px-3.5 py-1 rounded-bl-xl text-white ${
                            isCurrentPlanActive
                              ? 'bg-gradient-to-r from-[#1FAF8B] to-[#1E8A4C]'
                              : isEnterprise
                              ? 'bg-gradient-to-r from-[#5540A8] to-[#8069DA]'
                              : (billingCycle === 'forever'
                                ? 'bg-gradient-to-r from-[#FFB648] to-[#F2701D]'
                                : 'bg-gradient-to-r from-[#1E8A4C] to-[#2EAF66]')
                          }`}
                        >
                          {isCurrentPlanActive
                            ? '当前方案'
                            : (billingCycle === 'forever' && !isEnterprise ? '限时优惠' : '推荐')}
                        </div>

                        <div className="flex items-center gap-2">
                          {billingCycle === 'forever' && !isEnterprise && (
                            <span className="w-8 h-8 rounded-full bg-gradient-to-r from-[#FFB648] to-[#F2701D] flex items-center justify-center text-white text-sm">
                              👑
                            </span>
                          )}
                          <h3 className="text-base font-bold text-[#173C35]">
                            {activePlan?.plan_name || titleText}
                          </h3>
                          {isCurrentPlanActive ? (
                            <span className="text-[11px] px-2.5 py-0.5 rounded-full font-semibold text-[#1E8A4C] bg-[#2EAF66]/15 border border-[#2EAF66]/30">
                              当前方案
                            </span>
                          ) : (
                            <span
                              className={`text-[11px] px-2.5 py-0.5 rounded-full font-semibold ${
                                isEnterprise
                                  ? 'text-[#6D54C9] bg-[#F1EDFF]'
                                  : (billingCycle === 'forever'
                                    ? 'text-[#D95E0B] bg-[#FDF1E3]'
                                    : 'text-[#1E8A4C] bg-[#2EAF66]/10')
                              }`}
                            >
                              VIP
                            </span>
                          )}
                        </div>

                        {billingCycle === 'forever' && !isEnterprise && (
                          <p className="mt-1 text-xs text-[#8DA29C]">一次购买，永久享受</p>
                        )}

                        <div className="mt-4 flex items-end gap-2 flex-wrap">
                          <span className={`text-3xl font-bold bg-clip-text text-transparent ${textGradClass}`}>
                            ¥{showPrice}
                          </span>
                          <span className="text-[#8DA29C] text-xs mb-1">
                            / {billingCycle === 'monthly' ? '月' : (billingCycle === 'yearly' ? '年' : '永久')}
                          </span>
                          {billingCycle === 'yearly' && (
                            <span className="text-[#8DA29C] text-xs mb-1 line-through">
                              ¥{priceVal * 12}
                            </span>
                          )}
                          {billingCycle === 'forever' && !isEnterprise && (
                            <span className="text-[#8DA29C] text-xs mb-1 line-through">¥699</span>
                          )}
                        </div>

                        {billingCycle === 'yearly' && (
                          <p className="mt-1 text-[11px] text-[#1FAF8B] font-medium">
                            年付立省 20%，约 ¥{(showPrice / 12).toFixed(1)}/月
                          </p>
                        )}

                        {billingCycle === 'forever' && !isEnterprise && (
                          <div className="mt-3 flex items-center gap-2.5 bg-[#FDF1E3] rounded-xl p-3">
                            <span className="w-8 h-8 shrink-0 rounded-lg bg-gradient-to-r from-[#FFB648] to-[#F2701D] flex items-center justify-center text-white text-base">
                              🎁
                            </span>
                            <div>
                              <div className="text-xs font-bold text-[#D95E0B]">赠送 3000 积分</div>
                              <div className="text-[11px] text-[#647C75]">可用于购买 AI 视频等服务</div>
                            </div>
                          </div>
                        )}

                        <ul className="mt-5 space-y-2.5 text-xs sm:text-sm text-[#647C75] flex-1">
                          {activePlan?.benefits && activePlan.benefits.length > 0 ? (
                            activePlan.benefits.map((b, i) => {
                              const text = b.benefit_name || b.description || '';
                              if (!text) return null;
                              return (
                                <li key={i} className="flex items-start gap-2">
                                  <span
                                    className={`mt-0.5 shrink-0 w-3.5 h-3.5 rounded-full text-[9px] flex items-center justify-center text-white font-bold ${
                                      isEnterprise
                                        ? 'bg-gradient-to-r from-[#5540A8] to-[#8069DA]'
                                        : (billingCycle === 'forever'
                                          ? 'bg-gradient-to-r from-[#FFB648] to-[#F2701D]'
                                          : 'bg-gradient-to-r from-[#1E8A4C] to-[#2EAF66]')
                                    }`}
                                  >
                                    ✓
                                  </span>
                                  <span>{text}</span>
                                </li>
                              );
                            })
                          ) : (
                            fallbackFeatures.map((f, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span
                                  className={`mt-0.5 shrink-0 w-3.5 h-3.5 rounded-full text-[9px] flex items-center justify-center text-white font-bold ${
                                    isEnterprise
                                      ? 'bg-gradient-to-r from-[#5540A8] to-[#8069DA]'
                                      : (billingCycle === 'forever'
                                        ? 'bg-gradient-to-r from-[#FFB648] to-[#F2701D]'
                                        : 'bg-gradient-to-r from-[#1E8A4C] to-[#2EAF66]')
                                  }`}
                                >
                                  ✓
                                </span>
                                <span dangerouslySetInnerHTML={{ __html: f }}></span>
                              </li>
                            ))
                          )}
                        </ul>

                        <button
                          type="button"
                          disabled={submitting}
                          onClick={() => {
                            const targetId = activePlan?.id || 'demo_plan_id';
                            handleOrderPlan(targetId);
                          }}
                          className={`mt-6 w-full py-2.5 rounded-full text-xs sm:text-sm font-bold text-white transition-all duration-200 hover:opacity-90 active:scale-[.98] cursor-pointer ${btnGradClass}`}
                        >
                          {submitting
                            ? '订单创建中...'
                            : isCurrentPlanActive
                              ? '立即续费'
                              : (billingCycle === 'forever' && !isEnterprise
                                ? '立即开通永久会员'
                                : `立即开通${titleText}`)}
                        </button>
                      </div>
                    );
                  })()
                )}
              </div>

              {/* 认证引导 */}
              {user && user.accountType === 'individual' && isEnterprise && (
                <p className="text-center text-xs text-[#8DA29C] mt-5">
                  企业会员仅限企业认证用户开通，如需升级请先完成
                  <Link to="/certification" onClick={onClose} className="text-[#1E8A4C] hover:underline mx-1 font-medium">
                    企业认证
                  </Link>
                </p>
              )}

              {/* 权益对比表格 */}
              <div className="mt-12">
                <h3 className="text-center text-lg font-bold text-[#173C35]">会员权益对比</h3>
                <p className="text-center text-[#8DA29C] text-xs mt-1">选择最适合你的会员类型</p>
                <div className="mt-5 bg-white rounded-2xl border border-[#DCEAE5] overflow-hidden overflow-x-auto shadow-xs">
                  <table className="w-full min-w-[500px] text-xs sm:text-sm">
                    <thead>
                      <tr className="bg-gradient-to-r from-[#103B34] to-[#173C35] text-left">
                        <th className="px-5 py-3 font-medium text-white/70">权益项目</th>
                        <th className="px-5 py-3 font-medium text-white/70 text-center">免费版</th>
                        <th className="px-5 py-3 font-medium text-center text-[#7ED9A6]">个人会员</th>
                        <th className="px-5 py-3 font-bold text-center text-[#F1EDFF]">企业会员</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#EEF7F3]">
                      {compareRows.map((row) => (
                        <tr key={row.name} className="hover:bg-[#EEF7F3]/60 transition-colors">
                          <td className="px-5 py-3 text-[#315C53] font-medium">{row.name}</td>
                          <td className="px-5 py-3 text-center text-[#647C75]" dangerouslySetInnerHTML={{ __html: row.free }}></td>
                          <td className="px-5 py-3 text-center text-[#647C75]" dangerouslySetInnerHTML={{ __html: row.personal }}></td>
                          <td className="px-5 py-3 text-center text-[#315C53] font-medium bg-[#F1EDFF]/45" dangerouslySetInnerHTML={{ __html: row.enterprise }}></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ============ Tab 2：积分充值 ============ */}
          {activeTab === 'points' && (
            <div key="points" className="animate-fade-in">
              <div className="mt-6 flex items-end justify-between flex-wrap gap-2">
                <div>
                  <h3 className="text-lg font-bold flex items-center gap-2 text-[#173C35]">
                    <span className="w-7 h-7 rounded-lg bg-gradient-to-r from-[#2B5CB8] to-[#5B91F0] flex items-center justify-center text-white text-sm">
                      ◈
                    </span>
                    积分包充值
                  </h3>
                  <p className="text-[#8DA29C] text-xs mt-1">
                    积分可用于解锁视频、查看简历联系方式等付费场景
                  </p>
                </div>
                <span className="text-[11px] text-[#3267C5] bg-[#EAF1FF] border border-[#3267C5]/15 px-3 py-1 rounded-full font-medium">
                  积分永久有效，随充随用
                </span>
              </div>

              {loadingProducts ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-[#5B91F0]/40 border-t-[#2B5CB8] rounded-full animate-spin mb-3"></div>
                  <p className="text-xs text-[#8DA29C]">正在获取最新积分包...</p>
                </div>
              ) : (
                (() => {
                  const displayPkgs = products.length > 0 ? products : [
                    { id: 'p1', price: { amount: '80' }, grant_points: 300, display_tag: '' },
                    { id: 'p2', price: { amount: '120' }, grant_points: 1000, display_tag: '超值热卖' },
                    { id: 'p3', price: { amount: '200' }, grant_points: 1500, display_tag: '' },
                    { id: 'p4', price: { amount: '400' }, grant_points: 3000, display_tag: '最划算' },
                  ];

                  const currentSelId = selectedProductId || displayPkgs[1]?.id || displayPkgs[0]?.id;
                  const selectedPkgObj = displayPkgs.find(p => p.id === currentSelId) || displayPkgs[0];
                  const selectedPrice = Number(selectedPkgObj?.price?.amount || 120);
                  const selectedPts = selectedPkgObj?.grant_points || 1000;

                  return (
                    <>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5 mt-6">
                        {displayPkgs.map((pkg, idx) => {
                          const pkgPrice = Number(pkg.price?.amount || 0);
                          const pkgPoints = pkg.grant_points || ((pkg.base_points || 0) + (pkg.bonus_points || 0));
                          const isSelected = currentSelId === pkg.id;
                          const tag = pkg.display_tag || (idx === 1 ? '超值热卖' : idx === 3 ? '最划算' : '');
                          const unitPrice = pkgPoints > 0 ? (pkgPrice / pkgPoints).toFixed(2) : '0.00';

                          return (
                            <button
                              key={pkg.id || idx}
                              type="button"
                              onClick={() => setSelectedProductId(pkg.id || null)}
                              className={`relative bg-white rounded-2xl border-2 p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md cursor-pointer ${
                                isSelected
                                  ? 'border-[#3267C5] shadow-[0_12px_30px_-18px_rgba(50,103,197,0.32)]'
                                  : 'border-[#DCEAE5]'
                              }`}
                            >
                              {tag && (
                                <span
                                  className={`absolute -top-2.5 left-3 text-[9px] font-bold px-2 py-0.5 rounded-full ${
                                    tag === '最划算'
                                      ? 'bg-gradient-to-r from-[#2B5CB8] to-[#5B91F0] text-white'
                                      : 'bg-[#EAF1FF] text-[#3267C5]'
                                  }`}
                                >
                                  {tag}
                                </span>
                              )}

                              <div className="flex items-center gap-1.5 text-xl font-bold text-[#173C35]">
                                <span className="w-7 h-7 rounded-full bg-[#EAF1FF] text-[#3267C5] flex items-center justify-center text-xs font-bold">
                                  ◈
                                </span>
                                {pkgPoints.toLocaleString()}
                              </div>
                              <div className="text-[11px] text-[#8DA29C] mt-1">积分</div>

                              <div className="mt-3 flex items-end justify-between">
                                <span className="text-base font-bold text-[#173C35]">¥{pkgPrice}</span>
                                <span className="text-[10px] text-[#8DA29C]">≈{unitPrice}元/分</span>
                              </div>

                              <div
                                className={`absolute top-3 right-3 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                                  isSelected
                                    ? 'border-[#3267C5] bg-gradient-to-r from-[#2B5CB8] to-[#5B91F0]'
                                    : 'border-[#DCEAE5]'
                                }`}
                              >
                                {isSelected && (
                                  <span className="text-white text-[9px] font-bold">✓</span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      {/* 底部充值条 */}
                      <div className="mt-5 flex flex-col sm:flex-row items-center justify-between gap-3 bg-[#EAF1FF] rounded-2xl border border-[#3267C5]/10 p-4">
                        <div className="text-xs sm:text-sm text-[#647C75]">
                          已选：<span className="font-bold text-[#3267C5]">{selectedPts} 积分</span>
                          <span className="mx-2 text-[#8DA29C]">|</span>
                          应付：<span className="font-bold text-lg text-[#173C35]">¥{selectedPrice}</span>
                        </div>
                        <button
                          type="button"
                          disabled={submitting}
                          onClick={() => {
                            handleOrderPoint(selectedPkgObj.id);
                          }}
                          className="w-full sm:w-auto px-8 py-2.5 rounded-full bg-gradient-to-r from-[#2B5CB8] to-[#5B91F0] text-white text-xs sm:text-sm font-bold hover:opacity-90 active:scale-[.98] transition shadow-md shadow-[#3267C5]/20 cursor-pointer"
                        >
                          {submitting ? '处理中...' : '立即充值'}
                        </button>
                      </div>
                    </>
                  );
                })()
              )}
            </div>
          )}
        </main>

      {/* 沉浸全屏底部 Footer */}
      <footer className="shrink-0 border-t border-[#DCEAE5] bg-white text-[#8DA29C] text-xs py-8 text-center mt-auto">
        © 2026 灵搭岛 · 让创意和效率同频共振
      </footer>

      <PayModal onSuccess={() => setNotice('支付成功！您的会员权益与积分已成功到账。')} />
    </div>,
    document.body
  );
}
